<template>
    <DashboardSectionCard
        :title="t('vue.dashboard_overview_activity_title')"
        :subtitle="t('vue.dashboard_overview_activity_subtitle')"
        :link-href="activityLogHref"
        :link-label="t('vue.dashboard_overview_activity_link')"
    >
        <p v-if="items.length === 0" class="text-sm text-gray-600">
            {{ t("vue.dashboard_overview_activity_empty") }}
        </p>

        <div v-else class="grid">
            <div
                v-for="(item, index) in items"
                :key="`${item.eventCode}-${item.occurredAt}-${index}`"
                class="flex min-h-[52px] items-center gap-3 border-t border-gray-200 py-2 first:border-t-0 first:pt-0"
            >
                <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12.8px] bg-gray-100"
                >
                    <component
                        :is="resolveIcon(item.icon)"
                        class="h-[18px] w-[18px] text-gray-500"
                    />
                </div>
                <p
                    class="min-w-0 flex-1 truncate text-[13px] leading-[18px] text-gray-600"
                >
                    {{ resolveMessage(item) }}
                </p>
                <p class="shrink-0 text-[13px] leading-[18px] text-gray-400">
                    {{ formatRelativeTime(item.occurredAt) }}
                </p>
            </div>
        </div>
    </DashboardSectionCard>
</template>

<script lang="ts" setup>
import type { Component } from "vue";
import {
    CreditCardIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    UserGroupIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import DashboardSectionCard from "./DashboardSectionCard.vue";
import { useDashboardRelativeTime } from "../composables/useDashboardRelativeTime";
import type {
    DashboardActivityIcon,
    IActivityItem,
} from "@/types/Dashboard/IDashboardOverview";

withDefaults(
    defineProps<{
        items: IActivityItem[];
        activityLogHref?: string | null;
    }>(),
    {
        activityLogHref: null,
    },
);

const { t } = useTranslations();
const { formatRelativeTime } = useDashboardRelativeTime();

const resolveIcon = (icon: DashboardActivityIcon): Component => {
    switch (icon) {
        case "domain":
            return GlobeAltIcon;
        case "shield":
            return ShieldCheckIcon;
        case "team":
            return UserGroupIcon;
        default:
            return CreditCardIcon;
    }
};

const resolveMessage = (item: IActivityItem): string =>
    t(`vue.dashboard_overview_activity_${item.eventCode}`, {
        subject: item.subject ?? "",
    });
</script>
